// İSG uzmanı portalı — İzin detayı: Araçlar (api-contract §23). İzin aracı KOPYALAMAZ, BAĞLAR:
// "Araç Ekle" taşeronun araç kaydından seçtirir (+ yeni plaka), "İzinden çıkar" yalnız bağı kaldırır.
// `PermitPersonnelPanel.tsx`'i AYNALAR: gerçek `Table`, sütunlar `WorkPermitVehicleDto`'dan. Plaka
// hücresi ortak `VehicleSheet`'i (izin bağlamı) açan bir `<button>`; Aracı düzenle / İzinden çıkar
// satırın ⋮ menüsündedir. Araç ekleme panel düzeyinde `TableToolbar`'ın ghost "Araç Ekle" aksiyonu
// (ekranın TEK dolu düğmesi Onayla kalır). Sheet hedefi satır DEĞİL uuid saklar: her render'da
// `vehicles` prop'undan bulunur, aksi hâlde belge listesi/hazırlık onay-red-yüklemeden sonra
// tazelenmez. Odak desenleri `contractors/VehiclesPanel.tsx`'in AYNISI.

import {
  ConfirmDialog,
  EmptyState,
  PermitVehicleAddSheet,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableToolbar,
  Tag,
  VehicleFormSheet,
  type VehicleFormTarget,
  VehicleSheet,
  type WorkPermitVehicleDto,
  canEditPlate,
  cn,
  countReadyVehicles,
  formatProgress,
  permitsApi,
  useDocumentTypes,
  useRowRemovalFocus,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Truck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invalidateVehicleCaches } from '../../lib/vehicleCache';
import { useVehicleClient } from '../../lib/vehicleClient';
import { type ReviewSource, escapeStaysInReviewRow } from '../documents/DocumentReviewActions';
import { VehicleDocuments } from '../vehicles/VehicleDocuments';

interface PermitVehiclesPanelProps {
  permitUuid: string;
  /** İznin taşeronu — araç kaydı bu taşeron düzeyindedir (§23). */
  contractorUuid: string;
  vehicles: WorkPermitVehicleDto[];
  /** İznin tipinde istenen araç belge türleri — `vehicle` §27 kayıt kodları (§22/§23). */
  requiredVehicleDocuments: string[];
  /**
   * `vehicles`'ı taşıyan izin detayı sonucunun zaman damgaları — AYNI sorgu sonucundan gelir.
   * Panel kendi gözlemcisiyle okusaydı, panelin tek başına render'ında (ör. kayıt listesi yenilendi)
   * önbellekteki yeni damga eski `vehicles` prop'uyla buluşur ("yenileme bitti, satır duruyor") ve
   * bekleyen odak talebini satır kalkmadan düşürürdü.
   */
  source: ReviewSource;
}

export function PermitVehiclesPanel({
  permitUuid,
  contractorUuid,
  vehicles,
  requiredVehicleDocuments,
  source,
}: PermitVehiclesPanelProps) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const queryClient = useQueryClient();
  const vehicleClient = useVehicleClient(contractorUuid);
  const [addOpen, setAddOpen] = useState(false);
  const [sheetUuid, setSheetUuid] = useState<string | null>(null);
  const [formTarget, setFormTarget] = useState<VehicleFormTarget | null>(null);
  const [toUnlink, setToUnlink] = useState<WorkPermitVehicleDto | null>(null);
  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const plateOpenerRef = useRef<HTMLElement | null>(null);
  const plateButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const rowKeys = useMemo(() => vehicles.map((v) => v.uuid), [vehicles]);

  // Kayıt listesini ısıtır: ekleme çekmecesi AÇILDIĞINDA hazır olsun — anahtar ve sorgu çekmecenin
  // kendisiyle AYNI istemciden (`vehicleClient`), yani aynı önbellek girdisi.
  useQuery({
    queryKey: vehicleClient.registryKey,
    queryFn: vehicleClient.listRegistry,
    enabled: vehicleClient.registryEnabled,
    staleTime: 60_000,
  });
  const readyCount = countReadyVehicles(vehicles);
  const sheetVehicle = vehicles.find((v) => v.uuid === sheetUuid) ?? null;

  // Hedef araç listeden kalkınca saklanan uuid bayatlamasın: odak efekti örtü açık sanmasın ve
  // sonradan yeniden bağlanan aynı araç eski durumla kendiliğinden açılmasın.
  useEffect(() => {
    if (sheetUuid !== null && sheetVehicle === null) {
      setSheetUuid(null);
    }
  }, [sheetUuid, sheetVehicle]);

  // Satır menüsünden açılan yüzeyler (çıkar / düzenle) odağı o satırın plaka düğmesine döndürür;
  // çıkarılan satır listeden kalkınca ve diyalog kapanınca odak "Araç Ekle"ye iner (ortak durum
  // makinesi). Damgalar `source`'tan: `vehicles` ile AYNI sorgu sonucu (prop notu).
  const focus = useRowRemovalFocus({
    rowKeys,
    stamps: source,
    addButtonRef,
    dialogOpen: toUnlink !== null,
    otherSurfaceOpen: formTarget !== null || sheetVehicle !== null || addOpen,
  });

  const unlinkMutation = useMutation({
    mutationFn: (vehicleUuid: string) => permitsApi.removePermitVehicle(permitUuid, vehicleUuid),
    onSuccess: (_data, vehicleUuid) => {
      invalidateVehicleCaches(queryClient, contractorUuid);
      // Bağ sunucuda kalktı → satır yenilemede gidecek; diyaloğun kapanış dönüşü artık o satırın
      // plaka düğmesine DEĞİL, kalıcı "Araç Ekle"ye iner (yenileme ne zaman gelirse gelsin).
      focus.removed(vehicleUuid);
      setToUnlink(null);
    },
    // 404 VEHICLE_NOT_FOUND: bağ zaten yok (bayat liste) → tazele; satır yenilemede kalkar.
    onError: (_error, vehicleUuid) => {
      invalidateVehicleCaches(queryClient, contractorUuid);
      focus.removeFailed(vehicleUuid);
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <TableToolbar
        summary={
          vehicles.length > 0
            ? t('permits.vehicles.readySummary', { ready: readyCount, total: vehicles.length })
            : undefined
        }
        onNew={() => setAddOpen(true)}
        newLabel={t('permits.vehicles.add')}
        newButtonRef={addButtonRef}
      />

      {/* Gerekli araç belgeleri — Personel'in "requiredDocsLine" desenini AYNALAR (README Stage 5
          "one 12px muted line in the toolbar, not a boxed panel"). */}
      <p className="px-1 text-xs text-muted carbon:pl-4">
        {requiredVehicleDocuments.length === 0
          ? t('permits.vehicles.requiredDocumentsEmpty')
          : t('permits.vehicles.requiredDocsLine', {
              list: requiredVehicleDocuments.map((type) => label('vehicle', type)).join(', '),
            })}
      </p>

      {/* `relative`: başlıktaki `sr-only` metin taşan tabloda kaydırıcıdan kaçıp sayfayı kaydırmasın.
          Personel/Araçlar tek sütun yapısı (#12, esnek ad sütunu tarifi): plaka sütunu kalan
          genişliği alır ve kırpılır (≥sm `w-full` + `max-w-0`); düğme `w-fit` → alt çizgi/odak
          halkası yalnız metni kaplar, boş hücreyi değil. <sm sabit 10rem. */}
      <Table caption={t('permits.detail.section.vehicles')} className="relative">
        <TableHeader>
          <TableRow>
            <TableHead
              className="w-full truncate sm:max-w-0"
              title={t('permits.vehicles.form.field.plate')}
            >
              {t('permits.vehicles.form.field.plate')}
            </TableHead>
            <TableHead className="text-right">{t('permits.vehicles.col.documents')}</TableHead>
            <TableHead>{t('permits.vehicles.col.readiness')}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t('permits.common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {vehicles.length === 0 ? (
            <TableMessageRow colSpan={4}>
              <EmptyState icon={Truck} title={t('permits.vehicles.empty')} />
            </TableMessageRow>
          ) : (
            vehicles.map((vehicle) => {
              const satisfiedCount =
                requiredVehicleDocuments.length - vehicle.missingVehicleDocumentTypes.length;
              return (
                <TableRow key={vehicle.uuid}>
                  <TableCell truncate={false} className="sm:max-w-0">
                    <button
                      type="button"
                      ref={(el) => {
                        if (el) {
                          plateButtonRefs.current.set(vehicle.uuid, el);
                        } else {
                          plateButtonRefs.current.delete(vehicle.uuid);
                        }
                      }}
                      onClick={(e) => {
                        plateOpenerRef.current = e.currentTarget;
                        setSheetUuid(vehicle.uuid);
                      }}
                      title={vehicle.plate}
                      className="block w-40 truncate sm:w-fit sm:max-w-full text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {vehicle.plate}
                    </button>
                  </TableCell>
                  <TableCell
                    className={cn(
                      'tnum text-right',
                      requiredVehicleDocuments.length === 0 && 'text-muted',
                    )}
                  >
                    {formatProgress(satisfiedCount, requiredVehicleDocuments.length)}
                  </TableCell>
                  <TableCell>
                    <Tag color={vehicle.vehicleReady ? 'green' : 'red'}>
                      {vehicle.vehicleReady
                        ? t('permits.vehicles.ready')
                        : t('permits.vehicles.notReady')}
                    </Tag>
                  </TableCell>
                  {/* `w-12 px-2` — Personel sekmesinin ⋮ hücresiyle AYNI ölçü (base `px-4` + 32px tetikleyici 64px'e büyürdü). */}
                  <TableCell className="w-12 px-2">
                    <RowActionsMenu
                      ariaLabel={t('permits.vehicles.action.openMenu', { plate: vehicle.plate })}
                      items={[
                        {
                          key: 'edit',
                          label: t('vehicles.action.edit'),
                          onSelect: () => {
                            focus.rememberOpener(plateButtonRefs.current.get(vehicle.uuid) ?? null);
                            setFormTarget({
                              mode: 'edit',
                              vehicle,
                              plateLocked: !canEditPlate(
                                { hasBeenLinked: true, isPlateLocked: vehicle.isPlateLocked },
                                'host',
                              ),
                            });
                          },
                        },
                        {
                          key: 'unlink',
                          label: t('vehicles.unlink.action'),
                          destructive: true,
                          onSelect: () => {
                            unlinkMutation.reset();
                            focus.rememberOpener(plateButtonRefs.current.get(vehicle.uuid) ?? null);
                            setToUnlink(vehicle);
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <PermitVehicleAddSheet
        client={vehicleClient}
        open={addOpen}
        onOpenChange={setAddOpen}
        permitUuid={permitUuid}
        linkedUuids={rowKeys}
        restoreFocusRef={addButtonRef}
      />

      <VehicleSheet
        open={sheetVehicle !== null}
        onOpenChange={(open) => !open && setSheetUuid(null)}
        actor="host"
        context={sheetVehicle ? { kind: 'permit', vehicle: sheetVehicle } : null}
        restoreFocusRef={plateOpenerRef}
        onEscapeKeyDown={escapeStaysInReviewRow}
        documents={
          sheetVehicle ? (
            <VehicleDocuments
              contractorUuid={contractorUuid}
              vehicle={sheetVehicle}
              source={source}
            />
          ) : null
        }
      />

      {formTarget ? (
        <VehicleFormSheet
          key={formTarget.mode === 'edit' ? formTarget.vehicle.uuid : 'create'}
          client={vehicleClient}
          target={formTarget}
          onClose={() => setFormTarget(null)}
          restoreFocusRef={focus.menuRestoreRef}
        />
      ) : null}

      <ConfirmDialog
        open={toUnlink !== null}
        onOpenChange={(open) => !open && setToUnlink(null)}
        title={t('vehicles.unlink.title')}
        body={t('vehicles.unlink.body', { plate: toUnlink?.plate ?? '' })}
        confirmLabel={t('vehicles.unlink.confirm')}
        restoreFocusRef={focus.menuRestoreRef}
        loading={unlinkMutation.isPending}
        error={unlinkMutation.isError ? unlinkMutation.error : undefined}
        onConfirm={() => toUnlink && unlinkMutation.mutate(toUnlink.uuid)}
      />
    </div>
  );
}
